/* Drawing: turns layout geometry into plain SVG shape data (no DOM, no React).
   Every renderer (the SVG string in svg.ts, the React canvas in react/) draws
   from these shapes, so all outputs look the same. Strokes and fills use
   currentColor; set the color on the root <svg>. Classes worth styling:
   kohd-halo (paper-colored underlay so a later trace bridges an earlier one),
   kohd-label (word labels), kohd-selected (highlight). */

import type { Article, ConstructType } from './lexicon';
import type { Punctuation } from './parse';
import {
  BarShape, ChargeShape, CouplerShape, DOT_R, G, GroundShape, KohdLayout, LinkShape,
  NodeShape, NullShape, pathLength, Pt, ringRadius, RowShape, slicePath, WordShape,
} from './layout';

export type Attrs = Record<string, string | number | undefined>;

export interface Shape {
  tag: 'g' | 'line' | 'circle' | 'polyline' | 'polygon' | 'path' | 'rect' | 'text';
  /** SVG attribute names as written in markup (stroke-width, data-kohd-word); undefined values are skipped. */
  attrs: Attrs;
  children?: Shape[];
  text?: string;
}

export interface DrawOptions {
  /** Show each word's text under its glyph. */
  labels?: boolean;
  /** Key of a trace, link or word to mark with the kohd-selected class. */
  selected?: string | null;
  /** Temporary word offsets by word key (used to preview a drag). */
  offsets?: Record<string, Pt>;
}

export const STROKE = 2.2;
const C = 10; // lexicon circle radius

const n = (v: number) => Math.round(v * 100) / 100;
const poly = (pts: Pt[]) => pts.map((p) => `${n(p.x)},${n(p.y)}`).join(' ');
const INK: Attrs = { fill: 'none', stroke: 'currentColor', 'stroke-width': STROKE };

/** Point t along direction d from p, then side-step s across it. */
const along = (p: Pt, d: Pt, t: number, s = 0): Pt => ({ x: p.x + d.x * t - d.y * s, y: p.y + d.y * t + d.x * s });

const group = (attrs: Attrs, children: Shape[]): Shape => ({ tag: 'g', attrs, children });
const line = (a: Pt, b: Pt, extra: Attrs = {}): Shape => ({ tag: 'line', attrs: { x1: n(a.x), y1: n(a.y), x2: n(b.x), y2: n(b.y), ...INK, ...extra } });
const circle = (c: Pt, r: number, extra: Attrs = {}): Shape => ({ tag: 'circle', attrs: { cx: n(c.x), cy: n(c.y), r, ...INK, ...extra } });
const disc = (c: Pt, r: number, extra: Attrs = {}): Shape => ({ tag: 'circle', attrs: { cx: n(c.x), cy: n(c.y), r, fill: 'currentColor', ...extra } });
const polyline = (pts: Pt[], extra: Attrs = {}): Shape => ({ tag: 'polyline', attrs: { points: poly(pts), ...INK, 'stroke-linejoin': 'round', ...extra } });

/* ── Circuit parts (absolute coordinates) ─────────────────────────── */

/** Concentric rings of a node; "the" adds a line inside, "a"/"an" a plus. */
export function ringShapes(node: NodeShape, article?: Article): Shape[] {
  const rings = Array.from({ length: node.rings }, (_, i) => circle(node, ringRadius(i + 1), { class: 'kohd-ring' }));
  if (!article) return rings;
  const mark = [line({ x: node.x, y: node.y - 10 }, { x: node.x, y: node.y + 10 })];
  if (article === 'a') mark.push(line({ x: node.x - 10, y: node.y }, { x: node.x + 10, y: node.y }));
  return [...rings, group({ 'data-kohd-article': article }, mark)];
}

export const dotShapes = (dots: Pt[]): Shape[] => dots.map((d) => disc(d, DOT_R, { class: 'kohd-dot' }));

/** Charge indicator: a resistor zigzag on a short lead from the first node. */
export function chargeShapes(charge: ChargeShape): Shape[] {
  const { port, dir } = charge;
  const zig: Pt[] = [port, along(port, dir, 10)];
  for (let i = 0; i < 6; i++) zig.push(along(port, dir, 12 + i * 4, i % 2 === 0 ? 6 : -6));
  zig.push(along(port, dir, 34), charge.end);
  return [polyline(zig, { class: 'kohd-charge' })];
}

/** Ground indicator: a lead carrying the last letters' dots, then three stacked bars. */
export function groundShapes(ground: GroundShape): Shape[] {
  const bar = (t: number, w: number) => line(along(ground.lineEnd, ground.dir, t, -w / 2), along(ground.lineEnd, ground.dir, t, w / 2));
  return [group({ class: 'kohd-ground' }, [line(ground.port, ground.lineEnd), bar(0, 24), bar(5, 15), bar(10, 6)]), ...dotShapes(ground.dots)];
}

/** The simple bar that starts or ends every connection between words. */
export const barShapes = (bar: BarShape): Shape[] => [line(along(bar, bar.dir, 0, -9), along(bar, bar.dir, 0, 9), { 'stroke-width': 4.2 })];

/** Null modifier: a crossed circle with a stub pointing to the grid center. */
export function nullShapes(mod: NullShape): Shape[] {
  const k = 12 / 34; // stub starts at the circle edge
  return [group({ class: 'kohd-null' }, [
    circle(mod, 12),
    line({ x: mod.x - 12, y: mod.y }, { x: mod.x + 12, y: mod.y }),
    line({ x: mod.x, y: mod.y - 12 }, { x: mod.x, y: mod.y + 12 }),
    line({ x: mod.x + (mod.stubEnd.x - mod.x) * k, y: mod.y + (mod.stubEnd.y - mod.y) * k }, mod.stubEnd),
    circle(mod.tip, 4.5),
  ])];
}

/** Coupler: a column of bars where one sentence circuit can fuse to another. */
export const couplerShapes = (coupler: CouplerShape): Shape[] => [group({ class: 'kohd-coupler' }, coupler.bars.map((b) => ({
  tag: 'rect', attrs: { x: coupler.x - b.length, y: b.y, width: b.length, height: coupler.barHeight, fill: 'currentColor' },
})))];

/** Sentence-ending punctuation: a bar with a dot (period), triangle (exclamation) or hook (question). */
export function punctuationShapes(bar: BarShape, punctuation: Punctuation): Shape[] {
  if (punctuation === 'none') return [];
  const at = (t: number, s = 0) => along(bar, bar.dir, t, s);
  let mark: Shape[];
  if (punctuation === 'period') {
    mark = [line(bar, at(9)), disc(at(15), 6.5)];
  } else if (punctuation === 'exclamation') {
    mark = [{ tag: 'polygon', attrs: { points: poly([at(4), at(20, -9), at(20, 9)]), ...INK, 'stroke-linejoin': 'round' } }];
  } else {
    const [a, b, q, tip] = [at(2), at(18), at(22, 8), at(10, 8)];
    mark = [{ tag: 'path', attrs: { d: `M ${n(a.x)} ${n(a.y)} L ${n(b.x)} ${n(b.y)} Q ${n(q.x)} ${n(q.y)} ${n(tip.x)} ${n(tip.y)}`, ...INK } }];
  }
  return [group({ 'data-kohd-punctuation': punctuation }, [...barShapes(bar), ...mark])];
}

/* ── Lexicon symbols (local frame: the link enters at x = -half, leaves at x = +half) ── */

const seg = (x1: number, y1: number, x2: number, y2: number) => line({ x: x1, y: y1 }, { x: x2, y: y2 }, { 'stroke-linecap': 'round' });
const ring = (cx: number, r = C) => circle({ x: cx, y: 0 }, r);
const dot = (cx: number, cy = 0, r = 4.5) => disc({ x: cx, y: cy }, r);

/** Line ending in a one-sided barb (the book's half arrow). */
function halfArrow(x1: number, y1: number, x2: number, y2: number): Shape[] {
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const ux = (x2 - x1) / len;
  const uy = (y2 - y1) / len;
  return [seg(x1, y1, x2, y2), seg(x2, y2, x2 - ux * 5 + uy * 4, y2 - uy * 5 - ux * 4)];
}

/** And / Or: the link forks into two small input circles that join the main circle; Or leaves its lower input detached. */
function junction(half: number, broken: boolean): Shape[] {
  const input = (sy: number, detached: boolean) => [
    seg(-28, 0, -16, 12 * sy),
    circle({ x: -12, y: 12 * sy }, 4),
    seg(detached ? -3 : -8, 12 * sy, 2, 12 * sy),
    seg(2, 12 * sy, 6.93, 7.07 * sy),
  ];
  return [seg(-half, 0, -28, 0), ...input(-1, false), ...input(1, broken), ring(14), seg(24, 0, half, 0)];
}

/** "There is" has legs above and below its circle; "unique" only below. */
function legs(sy: number): Shape[] {
  return [seg(-7.1, 7.1 * sy, -12, 13 * sy), seg(-12, 13 * sy, -20, 13 * sy), seg(7.1, 7.1 * sy, 12, 13 * sy), seg(12, 13 * sy, 20, 13 * sy)];
}

export function constructShapes(type: ConstructType | 'comma', half: number, plural = false): Shape[] {
  switch (type) {
    case 'comma':
      return [seg(-half, 0, half, 0), seg(-3, -7, -3, 7), seg(3, -7, 3, 7)];
    case 'and':
    case 'or':
      return junction(half, type === 'or');
    case 'true':
      return [seg(-half, 0, -24, 0), ring(-14), ring(14), seg(-4, -3.2, 4, -3.2), seg(-4, 3.2, 4, 3.2), seg(24, 0, half, 0)];
    case 'false':
      return [seg(-half, 0, -24, 0), ring(-14), ring(14), seg(-5, 4, 1, -4), seg(-1, 4, 5, -4), seg(24, 0, half, 0)];
    case 'because':
      return [seg(-half, 0, -19.5, 0), dot(-15), ...halfArrow(-10, 0, 1, 0), ring(13), seg(23, 0, half, 0)];
    case 'so':
      return [seg(-half, 0, -23, 0), ring(-13), ...halfArrow(-3, 0, 8, 0), dot(14), seg(18.5, 0, half, 0)];
    case 'if':
      return [seg(-half, 0, -16, 0), ring(-6), ...halfArrow(4, 0, half, 0)];
    case 'ifThen':
      return [...halfArrow(-half, 0, -C, 0), ring(0), ...halfArrow(C, 0, half, 0)];
    case 'thereIs':
    case 'unique':
      return [seg(-half, 0, -C, 0), ring(0), seg(C, 0, half, 0), ...legs(1), ...(type === 'thereIs' ? legs(-1) : [])];
    case 'fromTo':
      return [ring(-19), polyline([{ x: -9, y: 0 }, { x: -4, y: 0 }, { x: -1, y: -8 }, { x: 2, y: 8 }, { x: 5, y: 0 }, { x: 9, y: 0 }]), ring(19)];
    case 'you':
    case 'youAll':
    case 'me':
    case 'we': {
      const up = type === 'you' || type === 'youAll';
      const xs = type === 'youAll' || type === 'we' ? [-3.5, 3.5] : [0];
      return [seg(-half, 0, -C, 0), ring(0), seg(C, 0, half, 0), ...xs.flatMap((x) => (up ? halfArrow(x, -C, x, -24) : halfArrow(x, C, x, 24)))];
    }
    case 'them':
    case 'their':
      return [
        seg(-half, 0, -16, 0), ring(-6),
        ...(type === 'their' ? [dot(-6, 0, 3.5)] : []),
        ...(plural ? [...halfArrow(4, -3.5, half, -3.5), ...halfArrow(4, 3.5, half, 3.5)] : halfArrow(4, 0, half, 0)),
      ];
    default:
      return [];
  }
}

/* ── Whole circuit ────────────────────────────────────────────────── */

const selectedClass = (key: string, opts: DrawOptions) => (opts.selected === key ? 'kohd-selected' : undefined);

/** A trace line with its paper-colored halo underneath. */
export const wireShapes = (points: Pt[]): Shape[] => [
  polyline(points, { class: 'kohd-halo', stroke: 'none', 'stroke-width': STROKE + 4, 'stroke-linecap': 'round' }),
  polyline(points, { class: 'kohd-trace', 'stroke-linecap': 'round' }),
];

/** A link between words: the line is cut where each symbol sits, and symbols turn with the link. */
export function linkShapes(link: LinkShape, opts: DrawOptions = {}): Shape {
  const total = pathLength(link.points);
  const runs: Pt[][] = [];
  let from = 0;
  link.items.forEach((item) => {
    if (item.s - item.half > from) runs.push(slicePath(link.points, from, item.s - item.half));
    from = item.s + item.half;
  });
  if (from < total) runs.push(slicePath(link.points, from, total));
  const symbols = link.items.map((item) => {
    const type = item.kind === 'comma' ? 'comma' : item.type;
    // Keep "up" up on links that run right to left, so pronoun arrows keep their meaning.
    const flip = Math.cos(item.angle) < -0.01 ? ' scale(1 -1)' : '';
    return group(
      { transform: `translate(${n(item.at.x)} ${n(item.at.y)}) rotate(${n((item.angle * 180) / Math.PI)})${flip}`, 'data-kohd-construct': type },
      constructShapes(type, item.half, item.kind === 'construct' && Boolean(item.plural)),
    );
  });
  return group({ 'data-kohd-link': link.key, class: selectedClass(link.key, opts) }, [...runs.flatMap(wireShapes), ...symbols]);
}

export function wordShapes(word: WordShape, opts: DrawOptions = {}): Shape {
  const isCharge = (p: Pt) => p.x === word.charge.node.x && p.y === word.charge.node.y;
  const offset = opts.offsets?.[word.key];
  return group({
    'data-kohd-word': word.text,
    class: selectedClass(word.key, opts),
    transform: offset ? `translate(${offset.x} ${offset.y})` : undefined,
  }, [
    ...word.traces.map((t) => group({ 'data-kohd-trace': t.key, class: selectedClass(t.key, opts) }, [...wireShapes(t.points), ...dotShapes(t.dots)])),
    ...chargeShapes(word.charge),
    ...barShapes(word.charge.bar),
    ...groundShapes(word.ground),
    ...(word.nullMod ? nullShapes(word.nullMod) : []),
    ...word.nodes.flatMap((node) => ringShapes(node, isCharge(node) ? word.charge.article : undefined)),
    ...(opts.labels ? [{ tag: 'text' as const, attrs: { class: 'kohd-label', x: word.label.x, y: word.label.y, 'text-anchor': 'middle' }, text: word.text }] : []),
  ]);
}

const startsAtCoupler = (row: RowShape, l: LinkShape) => l.start.x === row.coupler.x && l.start.y === row.coupler.y + G;

/** Punctuation sits on the final bar; a bare trailing link still ends in a simple bar. */
function rowEndShapes(row: RowShape): Shape[] {
  const { bar, punctuation } = row.end;
  if (!bar) return [];
  if (punctuation !== 'none') return punctuationShapes(bar, punctuation);
  const ground = row.words[row.words.length - 1]?.ground.bar;
  const isTail = !ground || ground.x !== bar.x || ground.y !== bar.y;
  return isTail ? barShapes(bar) : [];
}

export function rowShapes(row: RowShape, opts: DrawOptions = {}): Shape {
  return group({ 'data-kohd-row': row.text }, [
    ...couplerShapes(row.coupler),
    ...row.links.map((l) => linkShapes(l, opts)),
    ...row.links.filter((l) => !startsAtCoupler(row, l)).flatMap((l) => barShapes(l.start)),
    ...row.words.map((w) => wordShapes(w, opts)),
    ...rowEndShapes(row),
  ]);
}

/** Every shape of the circuit, in paint order. */
export const drawLayout = (layout: KohdLayout, opts: DrawOptions = {}): Shape[] => layout.rows.map((row) => rowShapes(row, opts));
